import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { api, type CompPublicPageResponse, type CompPublicPageBlob, type CompPublicPageComp } from '../lib/api';
import './CompHome.css';

// ─────────────────────────────────────────────────────────────────────────────
// CompHome — public competition home page.
//
// Two render paths, chosen at runtime:
//   1. DATA-DRIVEN — the comp has an authored + published public page (the blob
//      from GET /public/comp/:id). Every org authors its own page from the comp
//      editor; this renders it.
//   2. NEUTRAL FALLBACK — no published blob (un-authored comp, or an org that
//      hasn't built a page yet). Renders a clean, minimal, house-styled landing
//      from whatever comp data is available — never any single customer's
//      baked-in content. Every comp ships with default rules, so its
//      "Read the rules" link always resolves.
//
// Hooks live at the top, above every early return (a hook below a return crashes
// at runtime — "rendered more hooks than during the previous render").
//
// Reached from the Command Center comp card's "View competition page" button.
// The Register CTAs route back into the portal join flow.
// ─────────────────────────────────────────────────────────────────────────────

// ── Auth-gate message — shared by the unauthorized + forbidden states. ────────

function AuthGatePage({ type }: { type: 'unauthorized' | 'forbidden' }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ maxWidth: 400, textAlign: 'center' }}>
        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          {type === 'unauthorized' ? 'Sign in to view this page' : 'Members only'}
        </h2>
        <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
          {type === 'unauthorized'
            ? <>This page is only visible to members of the organisation's Discord server. Open the GG Anvil portal and sign in (run <code>/ggportal</code> in your Discord server to get a link), then reload.</>
            : "You need to be a member of this organisation's Discord server to view this page. If you belong to this org, ask an admin to add you to its Discord."}
        </p>
      </div>
    </div>
  );
}

export function CompHome() {
  const { compId } = useParams<{ compId: string }>();

  const [data, setData] = useState<CompPublicPageResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [authError, setAuthError] = useState<'unauthorized' | 'forbidden' | null>(null);

  useEffect(() => {
    let alive = true;
    if (!compId) {
      setLoaded(true);
      return;
    }
    api
      .publicCompPage(compId)
      .then(res => {
        if (alive) {
          setData(res);
          setLoaded(true);
        }
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const msg = err instanceof Error ? err.message : '';
        if (msg === 'unauthorized' || msg === 'forbidden') {
          // Auth gate — show the appropriate message; never fall through to the
          // neutral/public render on an auth failure.
          setAuthError(msg as 'unauthorized' | 'forbidden');
        } else {
          // not_found (unpublished / missing) or any other fetch error → neutral fallback.
          setData(null);
        }
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [compId]);

  // While the published-page probe is in flight, render the shell only — avoids a
  // flash of the neutral fallback before an authored page resolves.
  if (!loaded) {
    return <div className="comp-home" />;
  }

  // Auth errors — do not show competition content to unauthenticated / non-member visitors.
  if (authError) {
    return <AuthGatePage type={authError} />;
  }

  // Authored + published → data-driven render.
  if (data && data.page?.published) {
    return <DataDrivenCompHome compId={compId} comp={data.comp} page={data.page} />;
  }

  // No published blob → the neutral fallback. Reaches here for a logged-in
  // member viewing an un-authored comp (or one whose org hasn't built a page
  // yet). Never any single customer's hardcoded content.
  return <NeutralCompHome comp={data?.comp} compId={compId} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Data-driven render — from the authored public-page blob.
// Sections render only when their data is present, so a sparse page stays clean.
// Reuses the same CompHome.css classes so authored pages match the house look.
// ─────────────────────────────────────────────────────────────────────────────

function asText(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') return v.trim() || undefined;
  return String(v);
}

function paragraphs(text: string | undefined): string[] {
  if (!text) return [];
  return text
    .split(/\n{2,}|\r\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean);
}

function DataDrivenCompHome({
  compId,
  comp,
  page,
}: {
  compId?: string;
  comp: CompPublicPageComp;
  page: CompPublicPageBlob;
}) {
  const joinTo = compId ? `/?join=${encodeURIComponent(compId)}` : '/';
  const rulesTo = compId ? `/comp/${encodeURIComponent(compId)}/rules` : '#rules';
  const rulesAbs = compId ? `${window.location.origin}/comp/${encodeURIComponent(compId)}/rules` : undefined;

  const hero = page.hero ?? {};
  const title = asText(hero.title) ?? comp.name;
  const seasonTag = asText(hero.seasonTag);
  const subtitle = asText(hero.subtitle);
  const game = asText(comp.game);
  const prizePool = asText(comp.prizePool);
  const registerCta = asText(page.registerCtaText) ?? 'Register your team';

  const schedule = (page.schedule ?? []).filter(s => s && (s.label || s.date));
  const prizes = (page.prizes ?? []).filter(p => p && (p.place || p.prize));
  const sponsors = (page.sponsors ?? []).filter(s => s && (s.name || s.logoAssetUrl));
  const modes = (page.modes ?? []).map(m => asText(m)).filter((m): m is string => !!m);
  const aboutParas = paragraphs(asText(page.about));
  const rulesParas = paragraphs(asText(page.rules));
  const contact = page.contact ?? {};
  const keyDates = Object.entries(page.keyDates ?? {})
    .map(([k, v]) => [k, asText(v)] as const)
    .filter((e): e is readonly [string, string] => !!e[1]);

  const hasRules = rulesParas.length > 0;

  return (
    <div className="comp-home">
      {/* ── Top bar ── */}
      <header className="topbar">
        <div className="wrap">
          <div className="brand">
            {hero.logoAssetUrl ? <img src={hero.logoAssetUrl} alt={`${title} logo`} /> : null}
            <div className="name">{title}</div>
          </div>
          <Link to="/" className="backlink">← GG&nbsp;Anvil portal</Link>
        </div>
      </header>

      {/* ── Hero ── */}
      {/* has-banner adds the dark scrim (CompHome.css) so hero text stays
          readable over any banner image — same as the org page. */}
      <section
        className={hero.bannerAssetUrl ? 'hero has-banner' : 'hero'}
        style={hero.bannerAssetUrl ? { backgroundImage: `url(${hero.bannerAssetUrl})`, backgroundSize: 'cover', backgroundPosition: hero.bannerObjectPosition ?? 'center' } : undefined}
      >
        <div className="wrap">
          <div className="hero-lockup">
            {hero.logoAssetUrl ? <img className="hero-logo-img" src={hero.logoAssetUrl} alt={`${title} logo`} /> : null}
            <div>
              {seasonTag ? <div className="season-tag">{seasonTag}</div> : null}
              <h1>{title}</h1>
            </div>
          </div>
          {subtitle ? <p className="sub">{subtitle}</p> : null}
          <div className="chips">
            {comp.status ? <span className="pill"><span className="dot"></span> {comp.status}</span> : null}
            {comp.format ? <span className="pill">{comp.format}</span> : null}
            {game ? <span className="pill">{game}</span> : null}
            {prizePool ? <span className="pill">Prize pool: {prizePool}</span> : null}
          </div>
          <div className="cta-row">
            <Link className="btn btn-primary" to={joinTo}>{registerCta}</Link>
            {hasRules ? <a className="btn btn-ghost" href="#rules">Read the rules</a> : null}
            <CopyLinkButton label="Copy page link" />
          </div>
        </div>
      </section>

      {/* ── About ── */}
      {aboutParas.length > 0 ? (
        <section id="about">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>About the competition</div>
            <h2 className="sec-title">About {title}</h2>
            <div style={{ marginTop: 22 }}>
              {aboutParas.map((p, i) => (
                <p key={i} className="lead" style={i > 0 ? { marginTop: 14 } : undefined}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Key facts ── */}
      {(game || prizePool || comp.format || keyDates.length > 0) ? (
        <section id="facts" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>The essentials</div>
            <h2 className="sec-title">Key details</h2>
            <div className="grid g-4" style={{ marginTop: 22 }}>
              {game ? <div className="plate pad fact"><div className="k">Game</div><div className="v">{game}</div></div> : null}
              {comp.format ? <div className="plate pad fact"><div className="k">Format</div><div className="v">{comp.format}</div></div> : null}
              {prizePool ? <div className="plate pad fact"><div className="k">Prize pool</div><div className="v">{prizePool}</div></div> : null}
              {keyDates.map(([k, v]) => (
                <div key={k} className="plate pad fact"><div className="k">{k}</div><div className="v">{v}</div></div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Schedule ── */}
      {schedule.length > 0 ? (
        <section id="schedule">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>How the season runs</div>
            <h2 className="sec-title">Schedule &amp; rounds</h2>
            <div className="plate pad" style={{ marginTop: 22 }}>
              <div className="timeline">
                {schedule.map((s, i) => (
                  <div key={i} className="step">
                    <div className="num">{i + 1}</div>
                    <div>
                      <h3>{s.label}</h3>
                      {s.date ? <div className="when">{s.date}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Modes ── */}
      {modes.length > 0 ? (
        <section id="modes" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Competitive settings</div>
            <h2 className="sec-title">Game modes</h2>
            <div className="grid g-3" style={{ marginTop: 22 }}>
              {modes.map((m, i) => (
                <div key={i} className="plate pad mode">
                  <span className="label">Mode {String(i + 1).padStart(2, '0')}</span>
                  <h3>{m}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Prizes ── */}
      {prizes.length > 0 ? (
        <section id="prizes">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>What's on the line</div>
            <h2 className="sec-title">Prizes</h2>
            <div className="grid g-3" style={{ marginTop: 22 }}>
              {prizes.map((p, i) => (
                <div key={i} className="plate pad fact">
                  <div className="k">{p.place}</div>
                  <div className="v">{p.prize}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Rules ── */}
      {hasRules ? (
        <section id="rules" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Play fair, play hard</div>
            <h2 className="sec-title">Rules &amp; code of conduct</h2>
            <div className="plate pad" style={{ marginTop: 22 }}>
              {rulesParas.map((p, i) => (
                <p key={i} className="lead" style={i > 0 ? { marginTop: 14 } : undefined}>{p}</p>
              ))}
            </div>
            <div style={{ marginTop: 26, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link className="btn btn-primary" to={rulesTo}>Read the full rules</Link>
              <CopyLinkButton url={rulesAbs} label="Copy rules link" />
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Sponsors ── */}
      {sponsors.length > 0 ? (
        <section id="sponsors">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Backed by</div>
            <h2 className="sec-title">Partners</h2>
            <div className="sponsor-wall" style={{ marginTop: 18 }}>
              {sponsors.map((s, i) => {
                const inner = s.logoAssetUrl ? <img src={s.logoAssetUrl} alt={s.name} /> : <span>{s.name}</span>;
                return (
                  <div key={i} className="sponsor">
                    {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{inner}</a> : inner}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Contact ── */}
      {(contact.email || contact.discordInviteUrl || contact.note) ? (
        <section id="contact" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Get in touch</div>
            <h2 className="sec-title">Contact &amp; support</h2>
            <div className="plate pad" style={{ marginTop: 22 }}>
              {contact.note ? <p className="lead" style={{ marginBottom: 18 }}>{contact.note}</p> : null}
              <div className="kv">
                {contact.discordInviteUrl ? (
                  <>
                    <div className="k">Discord</div>
                    <div><a href={contact.discordInviteUrl} target="_blank" rel="noopener noreferrer">Join the Discord</a></div>
                  </>
                ) : null}
                {contact.email ? (
                  <>
                    <div className="k">Email</div>
                    <div><a href={`mailto:${contact.email}`}>{contact.email}</a></div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Register CTA ── */}
      <section id="register">
        <div className="wrap">
          <div className="plate pad" style={{ textAlign: 'center', padding: '48px 24px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--spectrum)' }}></div>
            {seasonTag ? <div className="season-tag">{seasonTag}</div> : null}
            <h2 className="display" style={{ fontSize: 'clamp(30px,5vw,52px)', fontWeight: 900, margin: '12px 0 10px' }}>Ready to compete?</h2>
            <p className="lead" style={{ margin: '0 auto 26px' }}>Register your team, claim your seats, and start forging your record.</p>
            <div className="cta-row" style={{ justifyContent: 'center' }}>
              <Link className="btn btn-primary" to={joinTo}>{registerCta}</Link>
              {hasRules ? <a className="btn btn-ghost" href="#rules">Read the rules first</a> : null}
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer>
        <div className="wrap foot">
          <div className="brand">
            {hero.logoAssetUrl ? <img src={hero.logoAssetUrl} alt={title} style={{ height: 40 }} /> : null}
            <div className="name">{title}{seasonTag ? <small>{seasonTag}</small> : null}</div>
          </div>
          <div className="links">
            {aboutParas.length > 0 ? <a href="#about">About</a> : null}
            {hasRules ? <a href="#rules">Rules</a> : null}
            {sponsors.length > 0 ? <a href="#sponsors">Sponsors</a> : null}
            {(contact.email || contact.discordInviteUrl || contact.note) ? <a href="#contact">Contact</a> : null}
          </div>
        </div>
        <div className="wrap" style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="copy">© 2026 {title}. All rights reserved.</div>
          <div className="copy">Tournament platform — Powered by GG&nbsp;Anvil™</div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NEUTRAL FALLBACK — a clean, minimal, house-styled competition landing built
// from whatever comp data is available. Rendered when a comp has no published
// public-page blob (un-authored, or an org that hasn't built one yet). Never
// bakes in any single customer's content — every org's un-authored comp gets
// the SAME neutral page. Every comp ships with default rules (see
// shared/defaultCompRules.ts), so the "Read the rules" link always resolves.
//
// `compId` is the route param (always available on this page) and is what
// drives the join/rules links; `comp` is the optional metadata the backend
// returns for the comp itself (name/status/format/game/prizePool) — the
// backend's GET /public/comp/:id currently only populates `comp` alongside a
// *published* page, so in practice `comp` is usually undefined here. That's
// fine: the page still renders a working generic hero + CTA off `compId`.
// ─────────────────────────────────────────────────────────────────────────────

function NeutralCompHome({ comp, compId }: { comp?: CompPublicPageComp; compId?: string }) {
  const joinTo = compId ? `/?join=${encodeURIComponent(compId)}` : '/';
  const rulesTo = compId ? `/comp/${encodeURIComponent(compId)}/rules` : '/';

  const title = comp?.name?.trim() || 'Competition';
  const game = asText(comp?.game);
  const prizePool = asText(comp?.prizePool);

  return (
    <div className="comp-home">
      {/* ── Top bar ── */}
      <header className="topbar">
        <div className="wrap">
          <div className="brand">
            <div className="name">{title}</div>
          </div>
          <Link to="/" className="backlink">← GG&nbsp;Anvil portal</Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="wrap">
          <div className="hero-lockup">
            <div>
              <h1>{title}</h1>
            </div>
          </div>
          <p className="sub">This competition&apos;s page hasn&apos;t been fully set up yet — register your team below to take part.</p>
          <div className="chips">
            {comp?.status ? <span className="pill"><span className="dot"></span> {comp.status}</span> : null}
            {comp?.format ? <span className="pill">{comp.format}</span> : null}
            {game ? <span className="pill">{game}</span> : null}
            {prizePool ? <span className="pill">Prize pool: {prizePool}</span> : null}
          </div>
          <div className="cta-row">
            <Link className="btn btn-primary" to={joinTo}>Register your team</Link>
            <Link className="btn btn-ghost" to={rulesTo}>Read the rules</Link>
            <CopyLinkButton label="Copy page link" />
          </div>
        </div>
      </section>

      {/* ── Register CTA ── */}
      <section id="register">
        <div className="wrap">
          <div className="plate pad" style={{ textAlign: 'center', padding: '48px 24px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--spectrum)' }}></div>
            <h2 className="display" style={{ fontSize: 'clamp(30px,5vw,52px)', fontWeight: 900, margin: '12px 0 10px' }}>Ready to compete?</h2>
            <p className="lead" style={{ margin: '0 auto 26px' }}>Register your team, claim your seats, and start forging your record.</p>
            <div className="cta-row" style={{ justifyContent: 'center' }}>
              <Link className="btn btn-primary" to={joinTo}>Register your team</Link>
              <Link className="btn btn-ghost" to={rulesTo}>Read the rules first</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer>
        <div className="wrap foot">
          <div className="brand">
            <div className="name">{title}</div>
          </div>
          <div className="links">
            <Link to={rulesTo}>Rules</Link>
          </div>
        </div>
        <div className="wrap" style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="copy">© 2026 {title}. All rights reserved.</div>
          <div className="copy">Tournament platform — Powered by GG&nbsp;Anvil™</div>
        </div>
      </footer>
    </div>
  );
}
